import { Select, type SelectProps } from '@mantine/core';
import { useCallback, useRef } from 'react';
import { US_STATES, resolveUsStateCode } from '@/pages/onboarding/constants';

export type UsStateSelectProps = Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> & {
  value: string;
  onChange: (value: string) => void;
};

/**
 * Searchable US state / territory Select used across address forms.
 * Keeps typed codes (e.g. "CA") on blur via Mantine autoSelectOnBlur + exact code resolution.
 */
export function UsStateSelect({
  value,
  onChange,
  onBlur,
  placeholder = 'Select state',
  nothingFoundMessage = 'No matching state',
  ...rest
}: UsStateSelectProps): JSX.Element {
  const searchRef = useRef('');

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const resolved = resolveUsStateCode(searchRef.current);
      if (resolved) {
        onChange(resolved);
      }
      searchRef.current = '';
      onBlur?.(event);
    },
    [onChange, onBlur],
  );

  return (
    <Select
      {...rest}
      data={US_STATES}
      searchable
      autoSelectOnBlur
      placeholder={placeholder}
      nothingFoundMessage={nothingFoundMessage}
      value={value || null}
      onChange={(next) => onChange(next ?? '')}
      onSearchChange={(query) => {
        searchRef.current = query;
      }}
      onBlur={handleBlur}
    />
  );
}
